import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import { api } from '../lib/api.js';
import { campaignsQuery } from '../lib/campaigns.js';

interface JourneySummary {
  id: string;
  name: string;
  description: string | null;
  status: string;
  version: number;
  createdAt: string;
}

interface Template {
  key: string;
  name: string;
  description: string;
  requires: Array<{ key: string; label: string; kind: 'campaign' | 'tag' | 'list' }>;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  paused: 'Pausiert',
  archived: 'Archiviert',
};

export function JourneysPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<Template | null>(null);

  const journeys = useQuery({
    queryKey: ['journeys'],
    queryFn: ({ signal }) => api.get<{ items: JourneySummary[] }>('/api/journeys', signal),
  });

  const templates = useQuery({
    queryKey: ['journeys', 'templates'],
    queryFn: ({ signal }) => api.get<{ items: Template[] }>('/api/journeys/templates', signal),
    staleTime: Infinity,
  });

  const create = useMutation({
    mutationFn: (input: { name: string; graph: unknown }) =>
      api.post<{ journey: JourneySummary }>('/api/journeys', input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['journeys'] });
      void navigate({ to: '/journeys/$id', params: { id: data.journey.id } });
    },
  });

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>Journeys</h1>
          <p className="muted text-sm">
            Mehrstufige Abläufe, die je Kontakt einzeln laufen. Änderungen gelten nur für neue
            Durchläufe — wer schon unterwegs ist, bleibt auf seiner Fassung.
          </p>
        </div>
      </header>

      {journeys.data && journeys.data.items.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Status</th>
                  <th scope="col">Fassung</th>
                </tr>
              </thead>
              <tbody>
                {journeys.data.items.map((journey) => (
                  <tr key={journey.id}>
                    <td>
                      <Link to="/journeys/$id" params={{ id: journey.id }}>
                        {journey.name}
                      </Link>
                      {journey.description && (
                        <div className="muted text-sm">{journey.description}</div>
                      )}
                    </td>
                    <td>
                      <span className="badge" data-campaign-status={journey.status}>
                        {STATUS_LABELS[journey.status] ?? journey.status}
                      </span>
                    </td>
                    <td data-numeric>v{journey.version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Aus Vorlage anlegen</h2>
          <p className="muted text-sm">
            Die Vorlage wird geprüft, bevor sie gespeichert wird — ein Ablauf mit unerreichbaren
            Knoten oder fehlendem Auslöser kommt gar nicht erst in die Datenbank.
          </p>
          <div className="preset-grid">
            {templates.data?.items.map((template) => (
              <button key={template.key} className="preset" onClick={() => setChosen(template)}>
                <strong>{template.name}</strong>
                <span className="muted text-sm">{template.description}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {chosen && (
        <TemplateDialog
          template={chosen}
          onClose={() => setChosen(null)}
          onCreate={(name, graph) => create.mutate({ name, graph })}
          busy={create.isPending}
          error={create.error instanceof Error ? create.error.message : null}
        />
      )}
    </div>
  );
}

/**
 * Die Vorlage braucht konkrete Bezüge — welche Kampagne, welches Tag. Erst
 * damit ergibt der Ablauf Sinn; ein Platzhalter würde beim Scharfschalten
 * scheitern.
 */
function TemplateDialog({
  template,
  onClose,
  onCreate,
  busy,
  error,
}: {
  template: Template;
  onClose: () => void;
  onCreate: (name: string, graph: unknown) => void;
  busy: boolean;
  error: string | null;
}) {
  const [name, setName] = useState(template.name);
  const [bindings, setBindings] = useState<Record<string, string>>({});

  const campaigns = useQuery(campaignsQuery);
  const tags = useQuery({
    queryKey: ['tags'],
    queryFn: ({ signal }) =>
      api.get<{ items: Array<{ id: string; name: string }> }>('/api/tags', signal),
  });
  const lists = useQuery({
    queryKey: ['lists'],
    queryFn: ({ signal }) =>
      api.get<{ items: Array<{ id: string; name: string }> }>('/api/lists', signal),
  });

  const build = useMutation({
    mutationFn: () =>
      api.post<{ graph: unknown }>(`/api/journeys/templates/${template.key}/build`, { bindings }),
  });

  const ready = template.requires.every((r) => bindings[r.key]);

  function optionsFor(kind: 'campaign' | 'tag' | 'list') {
    if (kind === 'campaign') return campaigns.data?.items ?? [];
    if (kind === 'tag') return tags.data?.items ?? [];
    return lists.data?.items ?? [];
  }

  async function submit() {
    const result = await build.mutateAsync();
    onCreate(name, result.graph);
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>{template.name}</h2>
          <p className="muted text-sm">{template.description}</p>

          {(error || build.error) && (
            <div className="alert" data-tone="danger" role="alert">
              {error ?? (build.error instanceof Error ? build.error.message : 'Fehler')}
            </div>
          )}

          <Field label="Name des Ablaufs" value={name} onChange={(e) => setName(e.target.value)} />

          {template.requires.map((requirement) => (
            <div className="field" key={requirement.key}>
              <label className="field__label" htmlFor={`bind-${requirement.key}`}>
                {requirement.label}
              </label>
              <select
                id={`bind-${requirement.key}`}
                className="input"
                value={bindings[requirement.key] ?? ''}
                onChange={(e) =>
                  setBindings((current) => ({ ...current, [requirement.key]: e.target.value }))
                }
              >
                <option value="">Bitte wählen …</option>
                {optionsFor(requirement.kind).map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <div className="dialog__actions">
            <Button variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button onClick={submit} disabled={!ready} loading={busy || build.isPending}>
              Ablauf anlegen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
